import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";
import { toCsv, downloadCsv, sanitizeForFilename } from "../lib/csv";
import { formatMoney } from "../lib/format";
import { useSettings } from "../SettingsContext";
import DocumentPreview, { isPreviewable } from "./DocumentPreview";
import { dueDateFor } from "./StatementsGrid";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const fmtMoney = (v, settings) => formatMoney(v, settings);

const normalizeTxAmounts = (list) =>
  (list || []).map((t) => {
    if (t.amount === "" || t.amount == null) return t;
    const n = typeof t.amount === "number" ? t.amount : parseFloat(t.amount);
    return isNaN(n) ? t : { ...t, amount: n.toFixed(2) };
  });

const StatementDetail = ({ account, statement, onBack, onUpdate, onExtract, extractingFileIds }) => {
  const { settings } = useSettings();
  const [stmt, setStmt] = useState(statement);
  const [files, setFiles] = useState([]);
  const [amountInput, setAmountInput] = useState(statement.amount != null ? String(statement.amount) : "");
  const [transactions, setTransactions] = useState(normalizeTxAmounts(statement.transactions || []));
  const [txDirty, setTxDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [panelWide, setPanelWide] = useState(false);
  const [editingAmountIdx, setEditingAmountIdx] = useState(null);
  const [editingAmountCol, setEditingAmountCol] = useState(null);
  const panelRef = useRef(null);

  const isBankAccount = account.account_type === "Bank Account";
  const splitCols =
    account.account_type === "Credit Card"
      ? { left: "Payments", right: "Purchases" }
      : isBankAccount
      ? { left: "Withdrawals", right: "Deposits" }
      : null;
  const useSplit = Boolean(splitCols) && panelWide;
  // Side where a "refund" can be marked: CC payments (left), Bank deposits (right)
  const refundSide = account.account_type === "Credit Card" ? "left" : isBankAccount ? "right" : null;

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setPanelWide(entries[0].contentRect.width >= 640);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/statements/${stmt.id}/files`);
      setFiles(res.data);
    } catch (error) {
      console.error("Failed to fetch statement files:", error);
    }
  }, [stmt.id]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const save = async (payload) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/statements/${stmt.id}`, payload);
      setStmt(res.data);
      return res.data;
    } catch (error) {
      console.error("Failed to save statement:", error);
      toast.error("Failed to save statement");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAmount = async () => {
    const val = amountInput === "" ? null : parseFloat(amountInput);
    if (val != null && (isNaN(val) || val < 0)) {
      toast.error("Amount must be a non-negative number");
      return;
    }
    const updated = await save({ amount: val });
    if (updated) {
      toast.success("Amount saved");
      if (onUpdate) onUpdate();
    }
  };

  const togglePaid = async () => {
    const updated = await save({ is_paid: !stmt.is_paid });
    if (updated && onUpdate) onUpdate();
  };

  const updateTx = (idx, field, value) => {
    setTransactions((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
    setTxDirty(true);
  };

  const addTx = () => {
    setTransactions((prev) => [...prev, { date: "", description: "", amount: "" }]);
    setTxDirty(true);
  };

  const removeTx = (idx) => {
    setTransactions((prev) => prev.filter((_, i) => i !== idx));
    setTxDirty(true);
  };

  const saveTransactions = async () => {
    const cleaned = transactions
      .filter((t) => (t.description || "").trim() !== "" || t.date || t.amount !== "" )
      .map((t) => ({
        ...(t.id ? { id: t.id } : {}),
        date: t.date || null,
        description: t.description || "",
        amount: t.amount === "" || t.amount == null ? null : parseFloat(t.amount),
        note: t.note || null,
        tag: t.tag || null,
        tag_auto: !!t.tag_auto,
        hst: !!t.hst,
        refund: !!t.refund,
        tax: t.tax === "" || t.tax == null ? null : parseFloat(t.tax),
      }));
    const updated = await save({ transactions: cleaned });
    if (updated) {
      setTransactions(normalizeTxAmounts(updated.transactions || []));
      setTxDirty(false);
      toast.success("Transactions saved");
    }
  };

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    fd.append("statement_id", stmt.id);
    try {
      await axios.post(`${API}/accounts/${account.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchFiles();
      setStmt((prev) => ({ ...prev, document_count: (prev.document_count || 0) + fileList.length }));
      toast.success("Statement document uploaded");
    } catch (error) {
      console.error("Failed to upload:", error);
      toast.error("Failed to upload. Check file size (max 10MB) and type.");
    } finally {
      setUploading(false);
    }
  };

  const extractFrom = async (file) => {
    if (transactions.length > 0 && !window.confirm("This will replace the current transaction list with data extracted from the document. Continue?")) {
      return;
    }
    const updated = await onExtract(stmt, file);
    if (updated) {
      setStmt(updated);
      setTransactions(normalizeTxAmounts(updated.transactions || []));
      setTxDirty(false);
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download file.");
    }
  };

  const due = dueDateFor(account, stmt.year, stmt.month);
  const total = transactions.reduce((s, t) => {
    const v = parseFloat(t.amount);
    return s + (isNaN(v) || t.refund ? 0 : v);
  }, 0);
  const leftTotal = transactions.reduce((s, t) => {
    const v = parseFloat(t.amount);
    return s + (!isNaN(v) && !t.refund && v < 0 ? Math.abs(v) : 0);
  }, 0);
  const rightTotal = transactions.reduce((s, t) => {
    const v = parseFloat(t.amount);
    return s + (!isNaN(v) && !t.refund && v >= 0 ? v : 0);
  }, 0);

  const amountInputProps = (t, idx) => ({
    type: "number",
    step: "0.01",
    placeholder: "0.00",
    "data-testid": `transaction-amount-${idx}`,
    className:
      `w-full border-0 bg-transparent text-sm ${t.refund ? "text-red-600 font-medium" : "text-gray-900"} text-right focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5`,
    onChange: (e) => updateTx(idx, "amount", e.target.value),
    onFocus: (e) => {
      setEditingAmountIdx(idx);
      const n = parseFloat(e.target.value);
      setEditingAmountCol(!isNaN(n) && n < 0 ? "left" : "right");
    },
    onBlur: (e) => {
      setEditingAmountIdx(null);
      setEditingAmountCol(null);
      const v = e.target.value;
      if (v === "" || v == null) return;
      const n = parseFloat(v);
      if (!isNaN(n)) updateTx(idx, "amount", n.toFixed(2));
    },
  });

  const splitDisplayValue = (t, idx) => {
    if (editingAmountIdx === idx) return t.amount ?? "";
    if (t.amount === "" || t.amount == null) return "";
    const n = parseFloat(t.amount);
    return isNaN(n) ? t.amount : Math.abs(n).toFixed(2);
  };

  return (
    <div className="h-full" data-testid="statement-detail" ref={panelRef}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-white">
        <button
          onClick={onBack}
          data-testid="statement-back-button"
          className="text-sm text-blue-600 hover:text-blue-800 font-medium mb-2 inline-flex items-center gap-1"
        >
          ← Back to {account.name}
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900" data-testid="statement-title">
              {MONTHS[stmt.month - 1]} {stmt.year} Statement
            </h2>
            <p className="text-sm text-gray-600">
              {account.name}
              {due && ` · Due ${due.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isBankAccount && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="statement-paid-checkbox"
                  className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  checked={stmt.is_paid}
                  onChange={togglePaid}
                  disabled={saving}
                />
                <span className={`text-sm font-medium ${stmt.is_paid ? "text-green-600" : "text-gray-600"}`}>Paid</span>
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Amount */}
        {!isBankAccount && (
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                data-testid="statement-amount-input"
                className="w-36 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
              />
            </div>
            <button
              onClick={saveAmount}
              disabled={saving}
              data-testid="statement-save-amount-button"
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        )}

        {/* Documents */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-900">Documents ({files.length})</h3>
            <div>
              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.pdf,.txt,.eml,.msg"
                onChange={(e) => handleUpload(e.target.files)}
                className="hidden"
                id="statement-file-upload"
                data-testid="statement-file-upload-input"
              />
              <label
                htmlFor="statement-file-upload"
                className="cursor-pointer text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
              >
                {uploading ? "Uploading..." : "+ Upload"}
              </label>
            </div>
          </div>
          {files.length === 0 ? (
            <p className="text-sm text-gray-500 py-3" data-testid="statement-no-documents">No documents attached to this statement.</p>
          ) : (
            <div className="space-y-1.5" data-testid="statement-documents-list">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg px-3 py-2" data-testid={`statement-document-${file.id}`}>
                  <div className="text-sm text-gray-900 truncate">{file.original_filename}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => extractFrom(file)}
                      disabled={file.extracted || extractingFileIds?.has(file.id)}
                      data-testid={`extract-transactions-${file.id}`}
                      title={file.extracted ? "This document has already been extracted. Upload a new document to extract again." : undefined}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {extractingFileIds?.has(file.id)
                        ? "Extracting..."
                        : file.extracted
                        ? "Already extracted"
                        : "Extract transactions"}
                    </button>
                    {isPreviewable(file) && (
                      <button
                        onClick={() => setPreviewFile(file)}
                        data-testid={`statement-preview-${file.id}`}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
                      >
                        Preview
                      </button>
                    )}
                    <button
                      onClick={() => downloadFile(file.id, file.original_filename)}
                      data-testid={`statement-download-${file.id}`}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transactions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-900">Transactions ({transactions.length})</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const acctType = account.account_type;
                  let amountHeaders;
                  let splitAmount;
                  if (acctType === "Credit Card") {
                    amountHeaders = ["payment", "purchase"];
                    splitAmount = (v) => {
                      if (v === "" || v == null) return { payment: "", purchase: "" };
                      const n = typeof v === "number" ? v : parseFloat(v);
                      if (isNaN(n)) return { payment: "", purchase: "" };
                      return n < 0
                        ? { payment: Math.abs(n).toFixed(2), purchase: "" }
                        : { payment: "", purchase: n.toFixed(2) };
                    };
                  } else if (acctType === "Bank Account") {
                    amountHeaders = ["withdrawal", "deposit"];
                    splitAmount = (v) => {
                      if (v === "" || v == null) return { withdrawal: "", deposit: "" };
                      const n = typeof v === "number" ? v : parseFloat(v);
                      if (isNaN(n)) return { withdrawal: "", deposit: "" };
                      return n < 0
                        ? { withdrawal: Math.abs(n).toFixed(2), deposit: "" }
                        : { withdrawal: "", deposit: n.toFixed(2) };
                    };
                  } else {
                    amountHeaders = ["amount"];
                    splitAmount = (v) => ({ amount: v ?? "" });
                  }
                  const rows = transactions.map((t) => ({
                    date: t.date || "",
                    description: t.description || "",
                    ...splitAmount(t.amount),
                    note: t.note || "",
                    tag: t.tag || "",
                    hst: t.hst ? "Yes" : "",
                    tax: t.tax != null && t.tax !== "" ? Number(t.tax).toFixed(2) : "",
                    refund: t.refund ? "Yes" : "",
                  }));
                  const csv = toCsv(rows, ["date", "description", ...amountHeaders, "note", "tag", "hst", "tax", "refund"]);
                  const monthName = MONTHS[stmt.month - 1];
                  const filename = `${sanitizeForFilename(account.name)}-${stmt.year}-${String(stmt.month).padStart(2, "0")}-${monthName}-transactions.csv`;
                  downloadCsv(filename, csv);
                  toast.success(`Exported ${rows.length} transaction${rows.length === 1 ? "" : "s"}`);
                }}
                disabled={transactions.length === 0}
                data-testid="export-transactions-csv"
                className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Export CSV
              </button>
              <button
                onClick={addTx}
                data-testid="add-transaction-button"
                className="text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-50"
              >
                + Add
              </button>
              {txDirty && (
                <button
                  onClick={saveTransactions}
                  disabled={saving}
                  data-testid="save-transactions-button"
                  className="text-xs font-medium bg-blue-600 text-white rounded-md px-2.5 py-1.5 hover:bg-blue-700 disabled:opacity-50"
                >
                  Save changes
                </button>
              )}
            </div>
          </div>

          {transactions.length === 0 ? (
            <p className="text-sm text-gray-500 py-3" data-testid="statement-no-transactions">
              No transactions yet. Add them manually or extract them from an uploaded document.
            </p>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="transactions-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-3 py-2 w-36 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Description</th>
                    {useSplit ? (
                      <>
                        <th className="px-3 py-2 w-32 font-medium text-right" data-testid="split-left-header">{splitCols.left}</th>
                        <th className="px-3 py-2 w-32 font-medium text-right" data-testid="split-right-header">{splitCols.right}</th>
                      </>
                    ) : (
                      <th className="px-3 py-2 w-32 font-medium text-right">Amount</th>
                    )}
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {transactions.map((t, idx) => (
                    <tr key={t.id || idx} className="group" data-testid={`transaction-row-${idx}`}>
                      <td className="px-3 py-1.5">
                        <input
                          type="date"
                          value={t.date || ""}
                          onChange={(e) => updateTx(idx, "date", e.target.value)}
                          data-testid={`transaction-date-${idx}`}
                          className="w-full border-0 bg-transparent text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="text"
                          value={t.description || ""}
                          placeholder="Description"
                          onChange={(e) => updateTx(idx, "description", e.target.value)}
                          data-testid={`transaction-description-${idx}`}
                          className="w-full border-0 bg-transparent text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-300 rounded px-1 py-0.5"
                        />
                      </td>
                      {useSplit ? (
                        (() => {
                          const n = parseFloat(t.amount);
                          const isLeft = !isNaN(n) && n < 0 && editingAmountIdx !== idx;
                          const cell = (
                            <input {...amountInputProps(t, idx)} value={splitDisplayValue(t, idx)} />
                          );
                          const inRefundSide = refundSide === (isLeft ? "left" : "right");
                          const toggle =
                            refundSide && inRefundSide && t.amount !== "" && t.amount != null ? (
                              <button
                                onClick={() => updateTx(idx, "refund", !t.refund)}
                                data-testid={`transaction-refund-toggle-${idx}`}
                                title={t.refund ? "Unmark refund" : "Mark as refund (excluded from income/Sales totals)"}
                                className={`flex-shrink-0 transition-opacity ${
                                  t.refund
                                    ? "text-red-500 hover:text-red-700"
                                    : "opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500"
                                }`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                                </svg>
                              </button>
                            ) : null;
                          const wrapped = (
                            <div className="flex items-center gap-0.5">
                              {toggle}
                              {cell}
                            </div>
                          );
                          return (
                            <>
                              <td className="px-3 py-1.5" data-testid={`transaction-left-cell-${idx}`}>{isLeft ? wrapped : null}</td>
                              <td className="px-3 py-1.5" data-testid={`transaction-right-cell-${idx}`}>{!isLeft ? wrapped : null}</td>
                            </>
                          );
                        })()
                      ) : (
                        <td className="px-3 py-1.5">
                          <input {...amountInputProps(t, idx)} value={editingAmountIdx === idx ? t.amount ?? "" : t.amount ?? ""} />
                        </td>
                      )}
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => removeTx(idx)}
                          data-testid={`delete-transaction-${idx}`}
                          className="text-gray-300 hover:text-red-500 text-sm font-bold"
                          title="Remove transaction"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  {useSplit ? (
                    <>
                      <tr>
                        <td className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" colSpan={2}>Total</td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-gray-900" data-testid="transactions-left-total">{fmtMoney(leftTotal, settings)}</td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-gray-900" data-testid="transactions-right-total">{fmtMoney(rightTotal, settings)}</td>
                        <td></td>
                      </tr>
                      <tr className="border-t border-gray-200">
                        <td className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" colSpan={2}>Net</td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-gray-900" colSpan={2} data-testid="transactions-total">{fmtMoney(total, settings)}</td>
                        <td></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" colSpan={2}>Total</td>
                      <td className="px-3 py-2 text-right text-sm font-bold text-gray-900" data-testid="transactions-total">{fmtMoney(total, settings)}</td>
                      <td></td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {previewFile && <DocumentPreview file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};

export default StatementDetail;
